// src/services/axiosConfig.ts
// Centralized axios configuration with 401 interceptor for token refresh

import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import {
  getAuthToken,
  refreshAccessToken,
  isRefreshRejected,
  redirectToLogin
} from './authService';
import { notifyConnectionProblem } from './sessionEvents';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_FLUX_API_URL,
  withCredentials: true // Always send cookies
});

// Request interceptor - add auth header
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - on a 401, refresh and retry once. The tab is signed
// out only when the server refuses the session (the refresh is rejected, or
// the retry with a fresh token is refused too); connection problems are
// surfaced but keep the user signed in.
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    const status = error.response?.status;

    if (!error.response || (status !== undefined && status >= 500)) {
      notifyConnectionProblem();
      return Promise.reject(error);
    }

    if (status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    // Don't retry for login or refresh endpoints
    if (
      originalRequest.url?.includes('/admin/login') ||
      originalRequest.url?.includes('/admin/refresh_token') ||
      originalRequest.url?.includes('/admin/check_auth_method') ||
      originalRequest.url?.includes('/admin/logout')
    ) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      redirectToLogin('retry_unauthorized', 401);
      return Promise.reject(error);
    }
    originalRequest._retry = true;

    try {
      const newToken = await refreshAccessToken();
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      if (isRefreshRejected(refreshError)) {
        redirectToLogin('refresh_rejected', 401);
      } else {
        notifyConnectionProblem();
      }
      return Promise.reject(refreshError);
    }
  }
);

export default api;

// Also export for GraphQL or other uses
export { api };
